import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { 
  Play, Lock, Unlock, ChevronDown, ChevronRight, Star, Clock, 
  BookOpen, Award, Users, CheckCircle, ArrowRight, Sparkles,
  Hand, Move, RotateCw, ArrowUp, Waves, Zap, Music, Heart,
  Eye, X, AlertCircle, Crown
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for chapters
const chapterIcons = {
  'hand': Hand,
  'move': Move,
  'rotate': RotateCw,
  'arrow-up': ArrowUp,
  'waves': Waves,
  'zap': Zap,
  'sparkles': Sparkles,
  'music': Music,
};

// Animated background particles
const FloatingElements = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-2 h-2 bg-amber-500/20 rounded-full"
        initial={{ 
          x: Math.random() * 100 + '%', 
          y: Math.random() * 100 + '%',
          scale: Math.random() * 0.5 + 0.5
        }}
        animate={{ 
          y: [null, '-20px', '20px', '-20px'],
          opacity: [0.2, 0.6, 0.2]
        }}
        transition={{ 
          duration: 5 + Math.random() * 5, 
          repeat: Infinity,
          delay: Math.random() * 3
        }}
      />
    ))}
  </div>
);

// Chapter Card Component
const ChapterCard = ({ chapter, index, isExpanded, onToggle, hasAccess, onWatchLesson }) => {
  const Icon = chapterIcons[chapter.icon] || BookOpen;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -50 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative"
    >
      {/* Chapter Number Line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 via-amber-500/50 to-transparent" />
      
      <div className="ml-8">
        {/* Chapter Header */}
        <button
          onClick={onToggle}
          className="w-full text-left group"
        >
          <div className="flex items-start gap-6 p-6 bg-gradient-to-r from-white/5 to-transparent rounded-2xl border border-white/10 hover:border-amber-500/30 transition-all">
            {/* Chapter Number */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform">
                  <Icon className="w-8 h-8 text-black" />
                </div>
                <div className="absolute -top-2 -left-2 w-8 h-8 rounded-full bg-black border-2 border-amber-500 flex items-center justify-center text-amber-500 font-bold text-sm">
                  {chapter.number}
                </div>
              </div>
            </div>
            
            {/* Chapter Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-xl md:text-2xl font-bold text-white group-hover:text-amber-400 transition-colors">
                  {chapter.title}
                </h3>
                {!hasAccess && !chapter.has_preview && (
                  <Lock className="w-5 h-5 text-gray-500" />
                )}
                {chapter.has_preview && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">
                    Free Preview
                  </span>
                )}
              </div>
              <p className="text-amber-400/80 font-medium mb-2">{chapter.subtitle}</p>
              <p className="text-gray-400 text-sm line-clamp-2">{chapter.description}</p>
              
              <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  {chapter.lesson_count} lessons
                </span>
              </div>
            </div>
            
            {/* Expand Arrow */}
            <motion.div
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0 p-2"
            >
              <ChevronRight className="w-6 h-6 text-gray-500 group-hover:text-amber-400" />
            </motion.div>
          </div>
        </button>
        
        {/* Expanded Lessons */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="py-4 space-y-2">
                {/* We'll load lessons when expanded */}
                <LessonList 
                  chapterId={chapter.id} 
                  hasAccess={hasAccess}
                  onWatch={onWatchLesson}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Lesson List Component
const LessonList = ({ chapterId, hasAccess: parentHasAccess, onWatch }) => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localHasAccess, setLocalHasAccess] = useState(false);
  
  // Check access from token directly
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role === 'admin') {
          setLocalHasAccess(true);
        }
      } catch (e) {
        console.error('Token parse error in LessonList:', e);
      }
    }
  }, []);
  
  // Use either parent's hasAccess or local check
  const hasAccess = parentHasAccess || localHasAccess;
  
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const res = await fetch(`${API}/api/pandeiro-course/chapter/${chapterId}`, { headers });
        const data = await res.json();
        setLessons(data.lessons || []);
      } catch (err) {
        console.error('Failed to fetch lessons:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLessons();
  }, [chapterId]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-2 ml-6">
      {lessons.map((lesson, idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: idx * 0.05 }}
          className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
            hasAccess || lesson.is_preview
              ? 'bg-white/5 hover:bg-white/10 cursor-pointer'
              : 'bg-white/[0.02] opacity-60'
          }`}
          onClick={() => (hasAccess || lesson.is_preview) && onWatch(chapterId, idx)}
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            hasAccess || lesson.is_preview
              ? 'bg-amber-500/20 text-amber-400'
              : 'bg-gray-800 text-gray-600'
          }`}>
            {hasAccess || lesson.is_preview ? (
              <Play className="w-5 h-5" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-white truncate">{lesson.title}</h4>
              {lesson.is_preview && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                  Preview
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 truncate">{lesson.description}</p>
          </div>
          
          {lesson.duration && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {lesson.duration}
            </span>
          )}
        </motion.div>
      ))}
      
      {lessons.length === 0 && !loading && (
        <div className="text-center py-4">
          {hasAccess ? (
            <p className="text-gray-500">No lessons available yet</p>
          ) : (
            <div className="space-y-2">
              <p className="text-gray-500">Login as admin to access all lessons</p>
              <button
                onClick={() => {
                  const token = localStorage.getItem('auth_token');
                  if (token) {
                    // Force reload to re-check access
                    window.location.reload();
                  } else {
                    window.location.href = '/admin';
                  }
                }}
                className="text-amber-400 hover:text-amber-300 underline text-sm"
              >
                {localStorage.getItem('auth_token') ? 'Refresh to check access' : 'Click here to login'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Video Player Modal
const VideoModal = ({ lesson, onClose, onNavigate }) => {
  if (!lesson) return null;
  
  // Extract Vimeo ID from URL
  const getVimeoEmbed = (url) => {
    if (!url) return null;
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (match) {
      return `https://player.vimeo.com/video/${match[1]}?autoplay=1`;
    }
    return url;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* FIXED Close Button - Always visible in top right corner */}
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[60] px-4 py-3 bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-2 font-bold text-white shadow-lg"
      >
        <X className="w-5 h-5" />
        Close
      </button>
      
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">{lesson.lesson?.title}</h2>
            <p className="text-gray-400">Chapter: {lesson.chapter_title}</p>
          </div>
        </div>
        
        {/* Video with side navigation buttons */}
        <div className="relative flex items-center gap-4">
          {/* Previous button - left side */}
          {lesson.prev_lesson !== null ? (
            <button 
              onClick={() => onNavigate(lesson.chapter_id, lesson.prev_lesson)}
              className="flex-shrink-0 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              title="Previous Lesson"
            >
              <ChevronRight className="w-6 h-6 text-white rotate-180" />
            </button>
          ) : <div className="w-12 flex-shrink-0" />}
          
          {/* Video */}
          <div className="flex-1 aspect-video bg-black rounded-2xl overflow-hidden">
            {lesson.lesson?.video_url ? (
              <iframe
                src={getVimeoEmbed(lesson.lesson.video_url)}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                <AlertCircle className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg">Video coming soon</p>
                <p className="text-sm">This lesson's video is being prepared</p>
              </div>
            )}
          </div>
          
          {/* Next button - right side */}
          {lesson.next_lesson !== null ? (
            <button 
              onClick={() => onNavigate(lesson.chapter_id, lesson.next_lesson)}
              className="flex-shrink-0 w-12 h-12 bg-amber-500 hover:bg-amber-600 rounded-full flex items-center justify-center transition-colors"
              title="Next Lesson"
            >
              <ChevronRight className="w-6 h-6 text-black" />
            </button>
          ) : <div className="w-12 flex-shrink-0" />}
        </div>
        
        {/* Description below */}
        <div className="mt-4 p-4 bg-white/5 rounded-xl">
          <p className="text-gray-300 text-sm">{lesson.lesson?.description}</p>
        </div>
        
        {/* Back to Course button */}
        <div className="mt-4 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
          >
            ← Back to Course
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Pandeiro Juggling Page
const PandeiroJugglingInner = () => {
  const hero = useHero('pandeiro-juggling');
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  
  // Function to check access from token
  const checkAccessFromToken = () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Token payload:', payload);
        if (payload.role === 'admin') {
          return { hasAccess: true, email: payload.email, role: payload.role };
        }
        return { hasAccess: false, email: payload.email, role: payload.role };
      } catch (e) {
        console.error('Token parse error:', e);
      }
    }
    return { hasAccess: false, email: null, role: null };
  };
  
  useEffect(() => {
    fetchCourseData();
    // Check access immediately on mount
    const tokenCheck = checkAccessFromToken();
    setDebugInfo(tokenCheck);
    if (tokenCheck.hasAccess) {
      setHasAccess(true);
    }
  }, []);
  
  // Check access when user auth state changes
  useEffect(() => {
    console.log('Auth state:', { isAuthenticated, user, isAdminResult: isAdmin?.() });
    
    if (isAuthenticated && isAdmin && isAdmin()) {
      console.log('Setting hasAccess to true via AuthContext');
      setHasAccess(true);
      setDebugInfo({ hasAccess: true, email: user?.email, role: 'admin', source: 'AuthContext' });
    } else {
      const tokenCheck = checkAccessFromToken();
      if (tokenCheck.hasAccess) {
        console.log('Setting hasAccess to true via token');
        setHasAccess(true);
        setDebugInfo({ ...tokenCheck, source: 'Token' });
      } else if (!authLoading) {
        setHasAccess(false);
        setDebugInfo({ hasAccess: false, source: 'None' });
      }
    }
  }, [isAuthenticated, user, authLoading]);
  
  const fetchCourseData = async () => {
    try {
      const res = await fetch(`${API}/api/pandeiro-course/info`);
      const data = await res.json();
      setCourseData(data);
    } catch (err) {
      console.error('Failed to fetch course:', err);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };
  
  const handleWatchLesson = async (chapterId, lessonIndex) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const res = await fetch(
        `${API}/api/pandeiro-course/lesson/${chapterId}/${lessonIndex}`,
        { headers }
      );
      
      if (!res.ok) {
        if (res.status === 403) {
          toast.error('Purchase the course to access this lesson');
          return;
        }
        throw new Error('Failed to load lesson');
      }
      
      const data = await res.json();
      setCurrentLesson(data);
    } catch (err) {
      console.error('Failed to load lesson:', err);
      toast.error('Failed to load lesson');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }
  
  const settings = courseData?.settings || {};
  const chapters = courseData?.chapters || [];

  return (
    <div className="min-h-screen bg-black">
      <AdminEditorLink to="/admin/pandeiro-curriculum" label="Edit curriculum" />
      {/* Admin Status Bar - Shows when logged in */}
      {debugInfo?.email && (
        <div className={`fixed top-0 left-0 right-0 z-[100] py-2 px-4 text-sm font-medium flex items-center justify-center gap-4 ${
          hasAccess ? 'bg-green-600' : 'bg-yellow-600'
        }`}>
          <span className="flex items-center gap-2">
            {hasAccess ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            {hasAccess 
              ? `✓ Admin Access Active: ${debugInfo.email}` 
              : `Logged in as: ${debugInfo.email} (${debugInfo.role || 'user'})`
            }
          </span>
          {!hasAccess && (
            <button
              onClick={() => {
                const tokenCheck = checkAccessFromToken();
                if (tokenCheck.hasAccess) {
                  setHasAccess(true);
                  setDebugInfo({ ...tokenCheck, source: 'Manual' });
                } else {
                  toast.error('Not logged in as admin. Please login at /admin first.');
                }
              }}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-white"
            >
              Refresh Access
            </button>
          )}
          {hasAccess && (
            <span className="text-green-200 text-xs">
              You have full access to all lessons
            </span>
          )}
        </div>
      )}
      
      {/* Hero Section */}
      <section className={`relative min-h-[80vh] flex items-center overflow-hidden ${debugInfo?.email ? 'pt-10' : ''}`}>
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={settings.hero_image || "https://www.mestrecurtispierre.com/courses/wp-content/uploads/2020/05/pandeiroalbum4a.jpg"}
            alt="Pandeiro"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black/80" />
        </div>
        
        <FloatingElements />
        
        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
              <Heart className="w-4 h-4 text-red-500" />
              <span className="text-amber-400 text-sm font-bold uppercase tracking-wider">
                <PageEditable path="hero.eyebrow" fallback={hero.kicker || "Preserving a Dying Art Form"} />
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-black text-white mb-4">
              <PageEditable path="hero.titlePre" fallback={hero.title_top || "The Art of"} />
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-500 to-red-500">
                <PageEditable path="hero.titleAccent" fallback={hero.title_bottom || "Pandeiro Juggling"} />
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-2xl mb-8 leading-relaxed">
              <PageEditable
                path="hero.description"
                as="span"
                multiline
                fallback={settings.description || "Master the ancient art of Pandeiro juggling - a spectacular skill that's fading in Brasil. Learn from Mestre Curtis Pierre, one of the last masters keeping this tradition alive."}
              />
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap gap-6 mb-10">
              <div className="flex items-center gap-2 text-gray-400">
                <BookOpen className="w-5 h-5 text-amber-500" />
                <span><strong className="text-white">{courseData?.total_chapters}</strong> Chapters</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Play className="w-5 h-5 text-amber-500" />
                <span><strong className="text-white">{courseData?.total_lessons}</strong> Lessons</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Award className="w-5 h-5 text-amber-500" />
                <span><strong className="text-white">Certificate</strong> on Completion</span>
              </div>
            </div>
            
            {/* CTA */}
            <div className="flex flex-wrap items-center gap-4">
              {hasAccess ? (
                <>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-bold">
                    <Unlock className="w-4 h-4" />
                    Full Access Unlocked
                  </div>
                  <button
                    onClick={() => {
                      setExpandedChapter(chapters[0]?.id);
                      document.getElementById('curriculum-section')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all flex items-center gap-2"
                  >
                    <Play className="w-5 h-5" />
                    Start Learning
                  </button>
                </>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row gap-3 items-center">
                    <button
                      onClick={() => navigate('/membership-agreement?plan=monthly')}
                      className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all flex items-center gap-2"
                      data-testid="enroll-monthly-btn"
                    >
                      <Unlock className="w-5 h-5" />
                      Start Monthly - ${settings.price || 25}/{settings.price_period || 'month'}
                    </button>
                    <span className="text-gray-500">or</span>
                    <button
                      onClick={() => navigate('/membership-agreement?plan=lifetime')}
                      className="px-8 py-4 bg-white/5 border-2 border-amber-500 text-amber-400 font-bold text-lg rounded-full hover:bg-amber-500/10 transition-all flex items-center gap-2"
                      data-testid="enroll-lifetime-btn"
                    >
                      <Crown className="w-5 h-5" />
                      Lifetime Access - ${(settings.one_time_price || 2500).toLocaleString()}
                    </button>
                  </div>
                  <span className="text-gray-500">or</span>
                  <button
                    onClick={() => {
                      setExpandedChapter(chapters[0]?.id);
                      document.getElementById('curriculum-section')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="px-6 py-4 border border-white/20 text-white font-bold rounded-full hover:bg-white/5 transition-all flex items-center gap-2"
                  >
                    <Eye className="w-5 h-5" />
                    Watch Free Preview
                  </button>
                  {/* Login hint for users who may be logged in but access not detected */}
                  <div className="w-full mt-4">
                    <button
                      onClick={() => {
                        // Force re-check access
                        const token = localStorage.getItem('auth_token');
                        if (token) {
                          try {
                            const payload = JSON.parse(atob(token.split('.')[1]));
                            if (payload.role === 'admin') {
                              setHasAccess(true);
                              toast.success('Admin access detected! Refreshing...');
                              window.location.reload();
                              return;
                            }
                          } catch (e) {}
                        }
                        toast.info('Please login as admin to access all content', {
                          action: {
                            label: 'Login',
                            onClick: () => navigate('/admin')
                          }
                        });
                      }}
                      className="text-sm text-amber-400/70 hover:text-amber-400 underline"
                    >
                      Already logged in as admin? Click here to refresh access
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50"
        >
          <ChevronDown className="w-8 h-8" />
        </motion.div>
      </section>

      {/* Why This Course Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-amber-500 font-bold uppercase tracking-wider text-sm">
              <PageEditable path="why.eyebrow" as="span" fallback="Why This Matters" />
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mt-4 mb-6">
              <PageEditable path="why.title" as="span" fallback="A Tradition Worth Preserving" />
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              <PageEditable
                path="why.subtitle"
                as="span"
                multiline
                fallback="Pandeiro juggling was once a celebrated art in Brasil - performed in streets, festivals, and rodas de samba. Today, fewer than a handful of masters remain. This course ensures this beautiful tradition lives on through you."
              />
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Star,
                title: "Learn from the Master",
                description: "Mestre Curtis Pierre brings 38 years of experience, teaching techniques passed down through generations."
              },
              {
                icon: BookOpen,
                title: "Complete Curriculum",
                description: "8 comprehensive chapters take you from basic hand positions to show-stopping combinations."
              },
              {
                icon: Users,
                title: "Join the Legacy",
                description: "Become part of a select group keeping this dying art alive for future generations."
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-8 rounded-3xl bg-white/5 border border-white/10 text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  <PageEditable path={`why.cards.${idx}.title`} as="span" fallback={item.title} />
                </h3>
                <p className="text-gray-400">
                  <PageEditable path={`why.cards.${idx}.description`} as="span" multiline fallback={item.description} />
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Curriculum */}
      <section id="curriculum-section" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-amber-500 font-bold uppercase tracking-wider text-sm">
              <PageEditable path="curriculum.eyebrow" as="span" fallback="The Journey" />
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mt-4">
              <PageEditable path="curriculum.title" as="span" fallback="Course Curriculum" />
            </h2>
          </motion.div>
          
          <div className="space-y-6">
            {chapters.map((chapter, idx) => (
              <ChapterCard
                key={chapter.id}
                chapter={chapter}
                index={idx}
                isExpanded={expandedChapter === chapter.id}
                onToggle={() => setExpandedChapter(
                  expandedChapter === chapter.id ? null : chapter.id
                )}
                hasAccess={hasAccess}
                onWatchLesson={handleWatchLesson}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-950 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-8">
              <Heart className="w-10 h-10 text-red-500" />
            </div>
            
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Become a <span className="text-amber-400">Guardian</span> of This Art
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              When you learn Pandeiro juggling, you're not just gaining a skill - you're 
              preserving a piece of Brasilian cultural heritage. Start your journey today.
            </p>
            
            {!hasAccess && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => navigate('/membership-agreement?plan=monthly')}
                  className="px-10 py-5 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-xl rounded-full hover:shadow-xl hover:shadow-amber-500/30 transition-all"
                  data-testid="enroll-monthly-btn-bottom"
                >
                  Start Monthly — ${settings.price || 25}/{settings.price_period || 'month'}
                </button>
                <span className="text-gray-500 text-lg">or</span>
                <button
                  onClick={() => navigate('/membership-agreement?plan=lifetime')}
                  className="px-10 py-5 bg-white/5 border-2 border-amber-500 text-amber-400 font-bold text-xl rounded-full hover:bg-amber-500/10 transition-all"
                  data-testid="enroll-lifetime-btn-bottom"
                >
                  Lifetime Access — ${(settings.one_time_price || 2500).toLocaleString()}
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {currentLesson && (
          <VideoModal
            lesson={currentLesson}
            onClose={() => setCurrentLesson(null)}
            onNavigate={handleWatchLesson}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';

import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

export const PandeiroJuggling = () => (
  <PageEditProvider slug="pandeiro-juggling">
    <PandeiroJugglingInner />
    <PageEditToolbar />
  </PageEditProvider>
);

export default PandeiroJuggling;
